import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Copy, Check, Wrench, X as XIcon } from "lucide-react";
import { computeDv, formatRut } from "../lib/rut";
import useSeo from "../lib/useSeo";

/**
 * Column layout — 10 buckets by millions:
 * < 10M, 10M, 20M, 30M, 40M, 50M, 60M, 70M, 80M, 90M
 * Each range generates ROWS_PER_RANGE valid RUTs.
 */
const RANGES = [
  { key: "0-10", label: "< 10 MM", min: 1_000_000, max: 9_999_999 },
  { key: "10-20", label: "10 MM", min: 10_000_000, max: 19_999_999 },
  { key: "20-30", label: "20 MM", min: 20_000_000, max: 29_999_999 },
  { key: "30-40", label: "30 MM", min: 30_000_000, max: 39_999_999 },
  { key: "40-50", label: "40 MM", min: 40_000_000, max: 49_999_999 },
  { key: "50-60", label: "50 MM", min: 50_000_000, max: 59_999_999 },
  { key: "60-70", label: "60 MM", min: 60_000_000, max: 69_999_999 },
  { key: "70-80", label: "70 MM", min: 70_000_000, max: 79_999_999 },
  { key: "80-90", label: "80 MM", min: 80_000_000, max: 89_999_999 },
  { key: "90-100", label: "90 MM", min: 90_000_000, max: 99_999_999 },
];

const ROWS_PER_RANGE = 10;

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

function generateBucket(min, max, count) {
  const seen = new Set();
  const out = [];
  while (out.length < count && seen.size < (max - min + 1)) {
    const n = randInt(min, max);
    if (seen.has(n)) continue;
    seen.add(n);
    const dv = computeDv(String(n));
    out.push(`${formatRut(String(n) + dv)}`);
  }
  return out;
}

function generateAll() {
  const grid = {};
  RANGES.forEach((r) => {
    grid[r.key] = generateBucket(r.min, r.max, ROWS_PER_RANGE);
  });
  return grid;
}

export default function Utilities() {
  useSeo({
    title: "Utilidades · Generador de RUT chileno válido | DoSoft",
    description:
      "Herramienta gratuita para generar RUT chilenos válidos y ficticios por rangos de millones. Ideal para testing, QA y demos. Con copiado al portapapeles.",
    canonical: "/utilidades",
  });

  // Ask search engines not to index this internal tool page.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el?.getAttribute("content");
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
    return () => {
      if (prev) el.setAttribute("content", prev);
      else el.parentNode?.removeChild(el);
    };
  }, []);

  const [grid, setGrid] = useState(() => generateAll());
  const [copied, setCopied] = useState(() => new Set());

  const total = useMemo(
    () => Object.values(grid).reduce((acc, arr) => acc + arr.length, 0),
    [grid],
  );

  const regenerate = () => {
    setGrid(generateAll());
    setCopied(new Set());
  };

  const copyOne = async (rut) => {
    try {
      await navigator.clipboard.writeText(rut);
      setCopied((prev) => {
        const next = new Set(prev);
        next.add(rut);
        return next;
      });
      toast.success(`Copiado: ${rut}`);
    } catch (e) {
      toast.error("No se pudo copiar");
    }
  };

  const unmark = (rut) => {
    setCopied((prev) => {
      const next = new Set(prev);
      next.delete(rut);
      return next;
    });
  };

  return (
    <div className="bg-ash-100 min-h-[70vh]">
      <main className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        {/* Title */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brand-slate font-mono mb-1 flex items-center gap-2">
            <Wrench size={12} strokeWidth={1.8} /> Herramientas DoSoft
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight text-ink-900">
            Generador de RUT
          </h1>
          <p className="text-ink-800/70 text-sm md:text-base mt-3">
            Genera RUT chilenos válidos por rango de millones, útiles para QA, testing y demos.
          </p>
        </div>

        {/* Rounded card containing toolbar + table + footer */}
        <div className="rounded-2xl overflow-hidden border border-ink-500 bg-white shadow-lg">
          {/* Toolbar */}
          <div className="bg-ink-900 text-ash-100 px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
            <p className="text-sm text-ash-300 leading-relaxed max-w-3xl">
              Haz clic sobre un RUT para copiarlo al portapapeles. El sistema marcará automáticamente
              los registros utilizados para facilitar tu seguimiento visual.
            </p>
            <button
              data-testid="rut-regenerate"
              onClick={regenerate}
              className="shrink-0 h-10 inline-flex items-center gap-2 bg-[#8DE86F] text-ink-900 font-semibold hover:bg-[#7CD660] px-4 rounded-lg text-sm"
            >
              <RefreshCw size={14} strokeWidth={2.4} /> Generar nuevos
            </button>
          </div>

          {/* Grid */}
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="bg-ink-800 text-ash-300">
                {RANGES.map((r) => (
                  <th
                    key={r.key}
                    className="px-1 py-3 text-[10px] uppercase tracking-widest font-mono text-center border-r border-ink-700 last:border-0"
                  >
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {Array.from({ length: ROWS_PER_RANGE }).map((_, rowIdx) => (
                <tr key={rowIdx} className="border-t border-ash-100/60">
                  {RANGES.map((r) => {
                    const rut = grid[r.key]?.[rowIdx];
                    if (!rut) return <td key={r.key} className="px-1 py-2" />;
                    const isCopied = copied.has(rut);
                    return (
                      <td
                        key={r.key}
                        className="px-1 py-1 text-center border-r border-ash-100/40 last:border-0"
                      >
                        <div
                          className={`w-full font-mono text-[12px] whitespace-nowrap rounded-md transition-all inline-flex items-center justify-center gap-1 ${
                            isCopied
                              ? "bg-[#DFF7E8] text-[#0E6B2A] border border-[#8CD9A5]"
                              : "text-ink-800/60 hover:text-brand-active hover:bg-brand/10 border border-transparent"
                          }`}
                        >
                          <button
                            data-testid={`rut-${r.key}-${rowIdx}`}
                            onClick={() => copyOne(rut)}
                            title={isCopied ? "Copiar de nuevo" : "Copiar al portapapeles"}
                            className={`flex-1 inline-flex items-center justify-center gap-1 py-1.5 ${isCopied ? "pl-2 pr-1" : "px-1"}`}
                          >
                            {isCopied && <Check size={10} strokeWidth={3} />}
                            {rut}
                          </button>
                          {isCopied && (
                            <button
                              data-testid={`rut-unmark-${r.key}-${rowIdx}`}
                              onClick={(e) => { e.stopPropagation(); unmark(rut); }}
                              title="Desmarcar"
                              className="pl-0.5 pr-1.5 py-1.5 text-[#0E6B2A]/60 hover:text-[#9B1C1C]"
                            >
                              <XIcon size={11} strokeWidth={2.5} />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer inside the card */}
          <div className="bg-ash-100/60 border-t border-ash-100/70 px-5 py-3 text-[11px] text-ink-800/60 font-mono flex items-center justify-between">
            <span>Algoritmo módulo 11 · {RANGES.length} rangos · {total} RUT generados</span>
            {copied.size > 0 && (
              <span className="text-[#0E6B2A]">{copied.size} copiados en esta sesión</span>
            )}
          </div>
        </div>

        <div className="mt-8 text-xs text-ink-800/50 whitespace-nowrap overflow-x-auto">
          <strong>Aviso:</strong> Estos RUT se generan aleatoriamente para propósitos de prueba. Si alguno coincide con un RUT real es pura coincidencia estadística. Usa esta herramienta únicamente en ambientes de desarrollo o QA.
        </div>
      </main>
    </div>
  );
}
